import React, { useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, LinearProgress,
} from '@mui/material';
import { Add, Refresh, Delete, ArrowBack, YouTube, Visibility, CloudUpload, PictureAsPdf, Edit } from '@mui/icons-material';
import { InvokeApi, apiList, postImg } from '../../services/ApiService';
import { Config } from '../../config/Config';
import ClassSectionGrid from '../../components/ClassSectionGrid';
import dayjs from 'dayjs';

export default function Assignments() {
  const [view, setView] = useState('grid');
  const [selected, setSelected] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    assignment_name: '', assignment_detail: '', subject: '',
    start_date: '', end_date: '', youtube_url: '',
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  // progress edit state
  const [progressDialog, setProgressDialog] = useState(false);
  const [progressItem, setProgressItem]     = useState(null);
  const [progressValue, setProgressValue]   = useState(0);
  const [savingProgress, setSavingProgress] = useState(false);

  const load = useCallback(async (cls, subj) => {
    if (!cls || !subj) { setRecords([]); return; }
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getAssignmentsByStdSecAcYear, {
      subject: subj,
      standard: cls.standard,
      section: cls.section,
      academic_year: cls.acYear,
    });
    setRecords(data?.assignments || []);
    setLoading(false);
  }, []);

  const loadSubjects = useCallback(async (cls) => {
    const data = await InvokeApi(Config.main_url + apiList.getSubjects, {
      standard: cls.standard, section: cls.section,
    });
    const subs = data?.subjects || [];
    setSubjects(subs);
    if (subs.length > 0) {
      setSelectedSubject(subs[0].name);
      load(cls, subs[0].name);
    }
  }, [load]);

  const handleSelect = (cls) => {
    setSelected(cls);
    setView('list');
    setSelectedSubject('');
    setRecords([]);
    loadSubjects(cls);
  };

  const handleSubjectChange = (subj) => {
    setSelectedSubject(subj);
    load(selected, subj);
  };

  const handleSave = async () => {
    if (!form.assignment_name || !form.subject) {
      alert('Assignment name and subject are required'); return;
    }
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addAssignment, {
      teacherId: '1',
      standard: selected.standard,
      section: selected.section,
      subject: form.subject,
      startDate: form.start_date,
      endDate: form.end_date,
      assignmentName: form.assignment_name,
      assignmentDetail: form.assignment_detail,
      academic_year: selected.acYear,
      youtube_url: form.youtube_url || '',
    });

    if (data?.code === '200') {
      // Upload PDF if provided
      if (pdfFile && data.assignment_id) {
        const fd = new FormData();
        fd.append('file', pdfFile);
        fd.append('id', data.assignment_id);
        await postImg(Config.main_url + apiList.uploadAssignmentFile, fd);
      }
      setDialog(false);
      setPdfFile(null);
      load(selected, selectedSubject);
    } else alert(data?.message || 'Failed');
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this assignment?')) return;
    await InvokeApi(Config.main_url + apiList.deleteAssignment, { id, status: 'Deleted' });
    load(selected, selectedSubject);
  };

  const openProgressDialog = (r) => {
    setProgressItem(r);
    setProgressValue(parseInt(r.progress ?? 0, 10));
    setProgressDialog(true);
  };

  const handleSaveProgress = async () => {
    if (!progressItem) return;
    setSavingProgress(true);
    const res = await InvokeApi(Config.main_url + apiList.updateAssignmentProgress, {
      id: progressItem.ID,
      progress: progressValue,
    });
    setSavingProgress(false);
    if (res?.code === '200') {
      setProgressDialog(false);
      setProgressItem(null);
      setRecords(prev => prev.map(r =>
        r.ID === progressItem.ID ? { ...r, progress: progressValue } : r
      ));
    } else {
      alert(res?.message || 'Failed to update progress');
    }
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  if (view === 'grid') {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Assignments</Typography>
          <Typography variant="body2" color="text.secondary">Select a class to view assignments</Typography>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3 }}>
          <ClassSectionGrid onSelect={handleSelect} />
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Assignments</Typography>
          <Typography variant="body2" color="text.secondary">
            {selected?.standard} - {selected?.section} · {selected?.acYear} · {records.length} assignments
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setView('grid')}
            sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Back</Button>
          <Tooltip title="Refresh">
            <IconButton onClick={() => load(selected, selectedSubject)} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />}
            onClick={() => { setForm({ assignment_name: '', assignment_detail: '', subject: selectedSubject, start_date: '', end_date: '', youtube_url: '' }); setPdfFile(null); setDialog(true); }}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add Assignment
          </Button>
        </Box>
      </Box>

      {subjects.length > 0 && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
          <CardContent sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography fontSize={13} fontWeight={600} color="text.secondary" mr={1}>Subject:</Typography>
            {subjects.map(s => (
              <Chip key={s.ID} label={s.name} clickable
                onClick={() => handleSubjectChange(s.name)}
                sx={{
                  fontWeight: 700, fontSize: 12,
                  background: selectedSubject === s.name ? '#1a237e' : '#e8eaf6',
                  color: selectedSubject === s.name ? 'white' : '#1a237e',
                  '&:hover': { background: selectedSubject === s.name ? '#1a237e' : '#c5cae9' },
                }}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
        ) : !selectedSubject ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">Select a subject to view assignments</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {['#', 'Assignment', 'Subject', 'Start Date', 'End Date', 'Progress', 'Files', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No assignments for {selectedSubject} in {selected?.standard} - {selected?.section}
                  </TableCell></TableRow>
                ) : records.map((r, i) => (
                  <TableRow key={r.ID || i} hover>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell>
                      <Typography fontWeight={600} fontSize={13}>{r.assignment_name}</Typography>
                      <Typography fontSize={11} color="text.secondary" noWrap sx={{ maxWidth: 220 }}>{r.assignment_detail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={r.subject || '—'} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.start_date ? dayjs(r.start_date).format('DD MMM YYYY') : '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.end_date ? dayjs(r.end_date).format('DD MMM YYYY') : '—'}</TableCell>
                    {/* Progress */}
                    <TableCell sx={{ minWidth: 150 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={parseInt(r.progress ?? 0, 10)}
                            sx={{
                              height: 7, borderRadius: 4,
                              backgroundColor: '#e8eaf6',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                background: parseInt(r.progress ?? 0, 10) === 100
                                  ? 'linear-gradient(90deg,#2e7d32,#43a047)'
                                  : 'linear-gradient(90deg,#1a237e,#1565c0)',
                              },
                            }}
                          />
                        </Box>
                        <Typography fontSize={11} fontWeight={700} color="#1a237e" sx={{ minWidth: 30 }}>
                          {r.progress ?? 0}%
                        </Typography>
                        <Tooltip title="Edit Progress">
                          <IconButton size="small" onClick={() => openProgressDialog(r)} sx={{ color: '#1565c0', p: 0.3 }}>
                            <Edit sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {r.youtube_url && (
                          <Tooltip title="Watch Video">
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<YouTube />}
                              href={r.youtube_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                background: '#FF0000',
                                borderRadius: 2,
                                fontWeight: 700,
                                fontSize: 10,
                                '&:hover': { background: '#cc0000' },
                              }}
                            >
                              Watch
                            </Button>
                          </Tooltip>
                        )}
                        {r.file_url && (
                          <Tooltip title="Open PDF / File">
                            <Button size="small" variant="contained" startIcon={<PictureAsPdf />}
                              href={Config.main_url + r.file_url} target="_blank" rel="noopener noreferrer"
                              sx={{ background: '#e53935', borderRadius: 2, fontWeight: 700, fontSize: 10, '&:hover': { background: '#b71c1c' } }}>
                              PDF
                            </Button>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => setViewItem(r)} sx={{ color: '#1a237e' }}>
                        <Visibility fontSize="small" />
                      </IconButton>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(r.ID)} sx={{ color: '#c62828' }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Add Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Add Assignment — {selected?.standard} - {selected?.section}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Assignment Name *" value={form.assignment_name} onChange={f('assignment_name')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Details" value={form.assignment_detail} onChange={f('assignment_detail')} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Subject *" value={form.subject} onChange={f('subject')}>
                <MenuItem value="">— Select Subject —</MenuItem>
                {subjects.map(s => <MenuItem key={s.ID} value={s.name}>{s.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Start Date" value={form.start_date} onChange={f('start_date')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="End Date" value={form.end_date} onChange={f('end_date')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="YouTube URL (optional)" value={form.youtube_url || ''} onChange={f('youtube_url')}
                placeholder="https://youtube.com/watch?v=..." />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{ border: '2px dashed #c5cae9', borderRadius: 2, p: 2.5, textAlign: 'center', cursor: 'pointer', background: '#f8f9ff', '&:hover': { borderColor: '#1a237e', background: '#e8eaf6' } }}
                onClick={() => document.getElementById('assignment-pdf-upload').click()}
              >
                <input
                  id="assignment-pdf-upload"
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  onChange={e => setPdfFile(e.target.files[0])}
                />
                <CloudUpload sx={{ fontSize: 32, color: '#9fa8da', mb: 0.5 }} />
                {pdfFile ? (
                  <Typography fontSize={13} fontWeight={700} color="#1a237e">{pdfFile.name}</Typography>
                ) : (
                  <>
                    <Typography fontSize={13} fontWeight={600} color="#1a237e">Click to attach a file (optional)</Typography>
                    <Typography fontSize={11} color="text.secondary">PDF, DOC, DOCX, PPT, PPTX, XLS, JPG, PNG</Typography>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => { setDialog(false); setPdfFile(null); }} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {viewItem?.assignment_name}
          <Chip label={viewItem?.subject || '—'} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700 }} />
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            {viewItem?.start_date && (
              <Chip label={`Start: ${dayjs(viewItem.start_date).format('DD MMM YYYY')}`} size="small"
                sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
            )}
            {viewItem?.end_date && (
              <Chip label={`Due: ${dayjs(viewItem.end_date).format('DD MMM YYYY')}`} size="small"
                sx={{ background: '#ffebee', color: '#c62828', fontWeight: 600 }} />
            )}
          </Box>

          {viewItem?.assignment_detail ? (
            <Box sx={{ p: 2, background: '#f5f6fa', borderRadius: 2, mb: 2, borderLeft: '4px solid #1a237e' }}>
              <Typography fontSize={13} color="#333" lineHeight={1.7} sx={{ whiteSpace: 'pre-wrap' }}>
                {viewItem.assignment_detail}
              </Typography>
            </Box>
          ) : (
            <Typography fontSize={13} color="text.secondary" mb={2}>No details provided.</Typography>
          )}

          {viewItem?.file_url && (
            <Box sx={{ mb: 2.5 }}>
              <Typography fontSize={12} fontWeight={700} color="#e53935" mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PictureAsPdf fontSize="small" /> Attached File
              </Typography>
              <Button variant="contained" startIcon={<PictureAsPdf />}
                href={Config.main_url + viewItem.file_url} target="_blank" rel="noopener noreferrer"
                sx={{ background: '#e53935', borderRadius: 2, fontWeight: 700, '&:hover': { background: '#b71c1c' } }}>
                Open File
              </Button>
            </Box>
          )}

          {viewItem?.youtube_url && (
            <Box>
              <Typography fontSize={12} fontWeight={600} color="#1a237e" mb={1}>📹 Video Resource</Typography>
              {(() => {
                const url = viewItem.youtube_url;
                const match = url.match(/(?:youtu\.be\/|v=|embed\/)([^#&?]{11})/);
                const ytId = match?.[1];
                return ytId ? (
                  <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden', mb: 1 }}>
                    <iframe
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                      src={`https://www.youtube.com/embed/${ytId}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={viewItem.assignment_name}
                    />
                  </Box>
                ) : (
                  <Button variant="contained" startIcon={<YouTube />} href={url} target="_blank"
                    sx={{ background: '#FF0000', borderRadius: 2, fontWeight: 700, '&:hover': { background: '#cc0000' } }}>
                    Watch on YouTube
                  </Button>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setViewItem(null)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Progress Edit Dialog ── */}
      <Dialog open={progressDialog} onClose={() => setProgressDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit fontSize="small" /> Edit Progress
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {progressItem && (
            <Box>
              <Typography fontSize={13} fontWeight={600} color="#333" mb={2} noWrap>
                {progressItem.assignment_name}
              </Typography>

              {/* Progress bar preview */}
              <Box sx={{ mb: 1.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{
                    height: 10, borderRadius: 5,
                    backgroundColor: '#e8eaf6',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: progressValue === 100
                        ? 'linear-gradient(90deg,#2e7d32,#43a047)'
                        : 'linear-gradient(90deg,#1a237e,#1565c0)',
                    },
                  }}
                />
                <Typography fontSize={13} fontWeight={700} color="#1a237e" textAlign="right" mt={0.5}>
                  {progressValue}%
                </Typography>
              </Box>

              {/* Quick preset buttons */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {[0, 25, 50, 75, 100].map(v => (
                  <Chip
                    key={v}
                    label={`${v}%`}
                    clickable
                    onClick={() => setProgressValue(v)}
                    sx={{
                      fontWeight: 700, fontSize: 12,
                      background: progressValue === v ? '#1a237e' : '#e8eaf6',
                      color: progressValue === v ? '#fff' : '#1a237e',
                    }}
                  />
                ))}
              </Box>

              {/* Manual input */}
              <TextField
                fullWidth size="small" type="number"
                label="Progress % (0–100)"
                value={progressValue}
                onChange={e => setProgressValue(Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)))}
                inputProps={{ min: 0, max: 100 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setProgressDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSaveProgress} variant="contained" disabled={savingProgress}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 100 }}>
            {savingProgress ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
